/**
 * components/rightbar/TextTabBody.tsx —— 文本预览 tab 正文（kind=`text`，认领 `sononux://file/` 前缀）。
 *
 * 真源：`102:frontend_src/src/components/rightbar/TextTabBody.vue`（504 行）——抄形态/尺寸/交互，译 Vue→React+TS。
 * 依据：设计件 §6.5（预览渲染与安全）／§6.4（文本预览）／§6.3（资源地址模型：地址即身份；
 *       对外动作走 HTTP 端点）／§2.4（正文座位输入 `useTabInfo()`；组件三戒）。
 * 🔴 **安全硬规则（§6.5，本组件逐条落地，extension 亦不豁免）**：
 *    · HTML ⇒ **Blob iframe** ＋ `sandbox="allow-scripts"`（**不含 `allow-same-origin`**）；
 *    · 图片 ⇒ **仅 Blob URL** 进 `<img>`；**SVG 绝不进 DOM**；
 *    · 预览**只读**（无编辑、无搜索接口）；**超限 ⇒ 报错，绝不截断**。
 * 一期读取模式（诚实登记）：`/{id}/pages`（文本分页）·`/{id}/bytes`·`/{id}/all` 属**目标态**（§11.4）
 *   ⇒ 本批只走**完整字节**（`/{id}/content`）；超 `MAX_PREVIEW_BYTES` 落**下载卡**（不截断）。
 * 一期类型覆盖（§6.5）：文本 / PDF / HTML / 图片 四类；其余 ⇒ **下载卡**（禁硬解二进制）。
 * 🆕 0919 G2-3：office 三件套（docx/pptx/xlsx）改走**伴生文本件**——有则文本预览、无则下载卡（**仍不硬解二进制**）。
 * 现状→新树（§2.4 表）：`FilePreview.vue`(444 行) 的功能**并入本座位**（不丢功能）。
 *
 * Vue→React 差异点（登记）：
 *   ① 零取数（任务书 §2-3）：源件 `load()` 的取数段（`fileMeta`／`loadFileText`／`loadFileBytes`／
 *      `loadPlainText`，102 `session/verbs.js`）**移宿主**⇒ 本件吃 `meta`/`fileText`/`fileBytes`/`plainText`
 *      四个结果面（verbs 返回同形）；**判定段逐字留在本件**（判型/超限/office 分支/错误码分行/下载卡缘由
 *      与源件 load() 同序同文案）。对象 URL 的建/收（`URL.createObjectURL`／`revokeBlob`）落在 effect
 *      ＋卸载清理（React 语义映射；同源件 revokeBlob/onBeforeUnmount）。
 *   ② `utils/file.js`（随包未投）⇒ `formatSize`（🆕 L-AU-HYG 归位 `./formatSize.ts`，本件再导出兼容桶口）、
 *      `buildFileUrl`/`MAX_PREVIEW_BYTES`/`MAX_LINE_NUMBER_LINES`＋扩展名判定词表**逐字译入本件**（102 同名件原文）；`rightbar/rendererRegistry.js`（声明式
 *      渲染器注册表）同因**逐字译入**（`RENDERER_REGISTRY`／`resolveRenderer`）——单源收编归 main。
 *   ③ 对外动作（`downloadArtifact`/`openArtifactInNewTab`，102 `utils/fileActions.js` 唯一实现，内含
 *      多端降级/签名 URL）⇒ `onDownload`/`onOpenNewTab` 回调（宿主同支）；回执文案逐字留本件。
 *      `onCopyPath` 的懒读 fileMeta 补位 ⇒ 可选 `onLoadStoragePath` 回调（无 ⇒ 如实「服务器路径不可用」）；
 *      懒读结果不回写 meta（React 派生制，差异登记）。
 *   ④ `v-model`（换行开关）⇒ `checked`+`onChange`；`v-text` ⇒ 文本子节点；`v-else-if` 链 ⇒ 条件链渲染。
 */
import React, { useEffect, useState } from "react";
import type { TabBodyProps } from "./TabBodySeat";
import { XgIcon } from "./FilesTabBody";
import { formatSize } from "./formatSize"; // 🆕 L-AU-HYG 归位：真源＝./formatSize.ts

// ── 换行开关（逐字承源件模块作用域块） ────────────────────────────────────────────
/**
 * 换行开关**按 tab 保存**（§6.5 布局细节：「换行开关按 tab 保存、初始开启」）。
 * 🔴 必须落在**模块作用域**（setup 顶层是逐实例执行 ⇒ 放那里等于没保存）。
 * 只在内存（与「停靠面状态只在内存」同族）；刷新即回默认开启。
 */
const WRAP_BY_TAB = new Map<string, boolean>();

function wrapPrefOf(tabId: string): boolean {
  return WRAP_BY_TAB.get(tabId) !== false;
}

function setWrapPref(tabId: string, value: boolean): void {
  WRAP_BY_TAB.set(tabId, value);
}

/** 清空换行偏好（仅测试用） */
export function _resetWrapPrefs(): void {
  WRAP_BY_TAB.clear();
}

// ── 文件工具纯件（逐字译自 102 `utils/file.js`；单源收编归 main） ─────────────────

/**
 * 字节 → 人类可读 sizeText（"1.8MB" 格式，对齐 UI设计.md 4.2）——
 * 🆕 L-AU-HYG 归位：真源＝`./formatSize.ts`（`artifactList.ts` 同取用；单源不复制）。
 * 本件**再导出**＝兼容 `index.ts` 桶口（L55）与旧引用面；行为与签名一字未变。
 */
export { formatSize };

/** 扩展名（小写，含点，如 ".pdf"） */
export function fileExt(name: string): string {
  const s = String(name || "");
  const i = s.lastIndexOf(".");
  if (i < 0) return "";
  return s.slice(i).toLowerCase();
}

/** 是否为图片（支持在线预览：img + 缩放） */
export function isImageFile(name: string): boolean {
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"].includes(fileExt(name).slice(1));
}

/** 是否为 PDF（完整字节交浏览器原生渲染器渲染——一期；`pdfjs` 硬解不在本批，同 PDF 渲染分支口径） */
export function isPdfFile(name: string): boolean {
  return fileExt(name) === ".pdf";
}

/** 是否为纯文本类（txt/md/json/代码等，fetch 带鉴权 → pre 展示） */
export function isTextFile(name: string): boolean {
  return ["txt", "md", "json", "yaml", "yml", "csv", "py", "js", "ts", "vue", "css", "html", "env", "toml", "cfg", "ini"].includes(
    fileExt(name).slice(1),
  );
}

/** 是否为 Office 文档（docx/xlsx/pptx，走伴生文本件） */
export function isOfficeFile(name: string): boolean {
  return ["docx", "xlsx", "pptx"].includes(fileExt(name).slice(1));
}

/** 是否为 HTML（预览走 **Blob iframe** ＋ `sandbox="allow-scripts"`，§6.5 安全硬规则） */
export function isHtmlFile(name: string): boolean {
  return fileExt(name) === ".html" || fileExt(name) === ".htm";
}

/**
 * 预览上限（§6.5：**超限 ⇒ 拒绝，不截断**——「静默截断的页读起来像整页」）。
 * 一期无 `/{id}/pages` 分页端点（目标态，§11.4）⇒ 整份读取；超此上限一律落「下载卡」，**绝不做静默截断**。
 */
export const MAX_PREVIEW_BYTES = 4 * 1024 * 1024;
/** 行号栏渲染护栏（行数过多时隐藏行号栏，**只影响行号装饰、不影响正文内容**） */
export const MAX_LINE_NUMBER_LINES = 50000;

/**
 * 构造文件内容 URL（预览/下载共用）。
 * @returns `/api/files/${fileId}/content`
 */
export function buildFileUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/content`;
}

// ── 声明式渲染器注册表（逐字译自 102 `rightbar/rendererRegistry.js`；U-1f 预览增强） ──────

/** 渲染器条目（match＝扩展名匹配（同步且廉价）；kind＝渲染器类别；load＝加载策略；reason＝下载卡原因） */
export interface RendererEntry {
  match: (filename: string) => boolean;
  kind: "html" | "image" | "card" | "pdf" | "text" | "office";
  load: "text" | "bytes" | "-";
  reason?: string;
}

/**
 * 🔴 **声明式单一表**（顺序即优先级，首个匹配生效）：新增类型只改表，禁散落 `if (ext === …)` 分支。
 * 🔴 **数据显示不完整 ⇒ 整段不显示（A6）**，禁半份。
 * 顺序即安全：**HTML 先于「文本类」**——`isTextFile` 词表含 `html`（FilePreview 期遗留），
 * 若按文本渲染，则「HTML ⇒ Blob iframe ＋ 沙箱」这条 §6.5 硬规则会被绕过。
 */
export const RENDERER_REGISTRY: ReadonlyArray<RendererEntry> = Object.freeze([
  // §6.5：HTML ⇒ Blob iframe + sandbox="allow-scripts"（不含 allow-same-origin）
  { match: isHtmlFile, kind: "html", load: "bytes" },
  // §6.5：图片（非 SVG）⇒ 仅 Blob URL 进 <img>
  { match: (filename) => isImageFile(filename) && !/\.svg$/i.test(filename), kind: "image", load: "bytes" },
  // §6.5 安全硬规则：SVG 标记绝不进应用 DOM 或 iframe ⇒ 下载卡
  {
    match: (filename) => isImageFile(filename) && /\.svg$/i.test(filename),
    kind: "card",
    load: "text",
    reason: "SVG 文件不支持在线预览（安全限制）",
  },
  // §6.5：PDF ⇒ 浏览器原生渲染
  { match: isPdfFile, kind: "pdf", load: "bytes" },
  // §6.5：文本/代码 ⇒ 累计文本页
  { match: isTextFile, kind: "text", load: "text" },
  // 0919 G2-3：Office 三件套 —— **不硬解二进制**，改走「伴生文本件」：有则文本预览、无则下载卡
  { match: isOfficeFile, kind: "office", load: "-", reason: "不支持预览" },
  // 🔴 兜底：未知扩展名 ⇒ 纯文本（A7 未知不猜）
  { match: () => true, kind: "text", load: "text" },
]);

/** 按文件名查找匹配的渲染器条目（首个匹配；兜底保底） */
export function resolveRenderer(filename: string): RendererEntry {
  for (const entry of RENDERER_REGISTRY) {
    if (entry.match(filename)) return entry;
  }
  // 理论上不会到达（最后一行是 () => true），防御性兜底
  return { match: () => true, kind: "text", load: "text" };
}

/** 视图类型判定（声明式注册表驱动，禁散落 if 分支；🆕 U-1f：改用 `resolveRenderer()` 查单一表） */
export function modeFor(filename: string): RendererEntry["kind"] {
  return resolveRenderer(filename).kind;
}

// ── 注入面类型（verbs 返回同形；零取数 ⇒ 宿主装载后注入） ─────────────────────────

/** `fileMeta` 结果（`{ok, data:{filename,size,ext,storage_path}, errorCode, errorText}`） */
export interface FileMetaResult {
  ok: boolean;
  data?: { filename?: string; size?: number; ext?: string; storage_path?: string };
  errorCode?: string | number | null;
  errorText?: string;
}

/** `loadFileText` 结果（`{ok, text, bytes, errorCode, errorText}`） */
export interface FileTextResult {
  ok: boolean;
  text?: string;
  bytes?: number;
  errorCode?: string | number | null;
  errorText?: string;
}

/** `loadPlainText`（office 伴生文本件）结果 */
export interface PlainTextResult {
  ok: boolean;
  text?: string;
}

/** `loadFileBytes` 结果（字节⇒Blob；对象 URL 的建/收在本件 effect） */
export interface FileBytesResult {
  ok: boolean;
  blob?: Blob | null;
  errorCode?: string | number | null;
  errorText?: string;
}

/** 对外动作目标（源件 `downloadArtifact`/`openArtifactInNewTab` 入参同形） */
export interface FileActionTarget {
  fileId: string;
  fileUrl: string;
  filename: string;
  size: number;
}

export interface TextTabBodyProps extends TabBodyProps {
  /** `fileMeta` 结果（缺省＝加载中） */
  meta?: FileMetaResult | null;
  /** office 伴生文本（`loadPlainText` 结果） */
  plainText?: PlainTextResult | null;
  /** 文本正文（`loadFileText` 结果） */
  fileText?: FileTextResult | null;
  /** 原始字节（`loadFileBytes` 结果；image/pdf/html 用） */
  fileBytes?: FileBytesResult | null;
  /** 重载（源件 load() ⇒ 零取数：宿主重拉后换注结果面） */
  onReload?: () => void;
  /** 下载（102 `utils/fileActions.js::downloadArtifact` 唯一实现 ⇒ 宿主同支） */
  onDownload?: (target: FileActionTarget) => Promise<boolean> | boolean;
  /** 新标签页打开（102 `openArtifactInNewTab` 唯一实现；★20260921 预览语义不追加 `download=1`） */
  onOpenNewTab?: (target: FileActionTarget) => Promise<boolean> | boolean;
  /** 复制服务器路径的懒读补位（源件缺 storagePath 时重取 fileMeta ⇒ 宿主回调） */
  onLoadStoragePath?: () => Promise<string | null> | string | null;
}

/** 元数据视图（承源件 meta ref 同形） */
interface FileMetaView {
  filename: string;
  size: number;
  ext: string;
  storagePath: string;
}

/** 视图态（承源件 view ref 值域：loading | text | image | pdf | html | card | error） */
export type FileViewMode = "loading" | "text" | "image" | "pdf" | "html" | "card" | "error";

/** 文本预览 tab 正文 */
export function TextTabBody(props: TextTabBodyProps): React.ReactElement {
  const { info } = props;
  /** 资源地址来自座位输入面（§6.3 id 基：`sononux://file/session/<sid>/id/<file_id>?rev=&line=`） */
  const address = info.address || "";
  /** 从地址解析身份（§6.3 id 基；`rev`/`line` 参数一期不参与判定） */
  const matched = /^sononux:\/\/file\/session\/([^/?#]+)\/id\/([^/?#]+)/.exec(address);
  const parsed = matched ? { sessionId: matched[1], fileId: matched[2] } : { sessionId: "", fileId: "" };
  const fileId = parsed.fileId;
  const contentUrl = fileId ? buildFileUrl(fileId) : "";
  const tabId = info.tabId;

  /** 换行开关（模块作用域按 tab 保存；初始开启） */
  const [wrapped, setWrapped] = useState(() => wrapPrefOf(tabId));
  /** 动作回执（复制路径/新标签页降级说明等） */
  const [notice, setNotice] = useState("");
  /** image/pdf/html 的对象 URL（源件 revokeBlob：建/收在本件，卸载即收） */
  const [blobUrl, setBlobUrl] = useState("");
  const bytesBlob = props.fileBytes && props.fileBytes.ok ? (props.fileBytes.blob ?? null) : null;
  useEffect(() => {
    if (!bytesBlob) {
      setBlobUrl("");
      return;
    }
    const url = URL.createObjectURL(bytesBlob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [bytesBlob]);
  useEffect(() => {
    setNotice(""); // 源件 load() 首行清回执（地址变化/重载）
  }, [address]);

  // ── 判定段（源件 load() 判序逐条在位；取数已移宿主，结果面由 props 注入） ─────────
  let meta: FileMetaView = { filename: "", size: 0, ext: "", storagePath: "" };
  let view: FileViewMode = "loading";
  let errorCode: string | number | null = null;
  let errorText = "";
  let cardReason = "";
  let text = "";
  let textBytes = 0;

  if (!fileId) {
    view = "error";
    errorText = "地址无效（未解析出 file_id）";
  } else if (!props.meta) {
    view = "loading";
  } else if (props.meta.ok !== true) {
    // 元数据失败 ⇒ **按错误码分行**（不降级猜测：文件名/大小是超限拒绝与类型判定的依据）
    view = "error";
    errorCode = props.meta.errorCode ?? null;
    errorText = props.meta.errorText || "读取文件元数据失败";
  } else {
    const d = props.meta.data ?? {};
    meta = {
      filename: d.filename || fileId,
      size: Number(d.size || 0),
      ext: d.ext || "",
      storagePath: d.storage_path || "",
    };
    const mode = modeFor(meta.filename);

    // 🔴 超限 ⇒ 拒绝，不截断（§6.5 上限语义）
    if (meta.size > MAX_PREVIEW_BYTES) {
      view = "card";
      cardReason = `文件 ${formatSize(meta.size)} 超出预览上限 ${formatSize(MAX_PREVIEW_BYTES)}（不截断显示）`;
    } else if (mode === "office") {
      // 🆕 0919 G2-3：office 三件套 —— 有「伴生文本件」⇒ 文本预览；无 ⇒ 下载卡（不硬解二进制）
      const pr = props.plainText;
      if (!pr) {
        view = "loading";
      } else if (pr.ok && typeof pr.text === "string" && pr.text.length > 0) {
        text = pr.text;
        textBytes = new Blob([pr.text]).size;
        view = "text";
      } else {
        view = "card";
        cardReason = pr.ok ? "该文件无文本内容（不可预览）" : "该文件不含伴生文本（不可预览）";
      }
    } else if (mode === "card") {
      // 🆕 U-1f：card 模式——未支持类型／SVG ⇒ 下载卡（§6.5 类型覆盖）
      view = "card";
      cardReason = resolveRenderer(meta.filename).reason || "不支持预览";
    } else if (mode === "text") {
      const r = props.fileText;
      if (!r) {
        view = "loading";
      } else if (!r.ok) {
        view = "error";
        errorCode = r.errorCode ?? null;
        errorText = r.errorText || "读取失败";
      } else {
        text = r.text || "";
        textBytes = r.bytes || 0;
        view = "text";
      }
    } else {
      // image / pdf / html ⇒ 原始字节（图片仅 Blob URL；HTML 走沙箱 iframe）
      const r = props.fileBytes;
      if (!r) {
        view = "loading";
      } else if (!r.ok) {
        view = "error";
        errorCode = r.errorCode ?? null;
        errorText = r.errorText || "读取失败";
      } else {
        view = mode;
      }
    }
  }

  const sizeText = formatSize(meta.size);
  const lineCount = text ? text.split("\n").length : 0;
  /** 行号栏（单节点文本；`user-select: none` ⇒ 复制文本不含行号；行数过多则隐藏装饰） */
  const gutterText = (() => {
    if (!text) return "";
    if (lineCount > MAX_LINE_NUMBER_LINES) return "";
    const out = new Array<string>(lineCount);
    for (let i = 0; i < lineCount; i += 1) out[i] = String(i + 1);
    return out.join("\n");
  })();

  // ── 对外动作（§6.3 对外边界：一律 HTTP 端点；纯 Web UI，无本地桌面能力） ─────────

  function actionTarget(): FileActionTarget {
    return { fileId, fileUrl: contentUrl, filename: meta.filename || fileId, size: meta.size };
  }

  /** 下载（多端降级；成功/失败以回执文案兑现，不静默） */
  async function onDownload(): Promise<void> {
    setNotice("正在下载…");
    const ok = props.onDownload ? await props.onDownload(actionTarget()) : false;
    setNotice(ok ? "已开始下载" : "下载失败（可稍后重试）");
  }

  /**
   * 在新标签页打开（**预览**语义 · ★20260921 与 ArtifactBlock 同口径：签名 URL 不追加 `download=1`；
   * ★20260922 与正文区**同一支实现**）。拿不到签名 ⇒ 明说，不静默失败。
   */
  async function onOpenNewTab(): Promise<void> {
    const ok = props.onOpenNewTab ? await props.onOpenNewTab(actionTarget()) : false;
    if (!ok) setNotice("无法获取签名链接（请先登录或改用下载）");
  }

  /** 复制路径＝**服务器侧路径**（§6.3：UI 文案须带「服务器」字样，免误认为本机路径） */
  async function onCopyPath(): Promise<void> {
    let p = meta.storagePath;
    if (!p && props.onLoadStoragePath) {
      const v = await props.onLoadStoragePath();
      p = typeof v === "string" ? v : "";
    }
    if (!p) {
      setNotice("服务器路径不可用");
      return;
    }
    try {
      await navigator.clipboard.writeText(p);
      setNotice(`已复制服务器路径：${p}`);
    } catch {
      setNotice(`服务器路径：${p}`);
    }
  }

  function onToggleWrap(): void {
    const next = !wrapped;
    setWrapPref(tabId, next);
    setWrapped(next);
  }

  return (
    <div className="xg-text-tab" data-testid="rightbar-text">
      <header className="xg-text-tab__head">
        <span className="xg-text-tab__name" title={meta.filename} data-testid="rightbar-text-name">
          {meta.filename || fileId}
        </span>
        {sizeText ? <span className="xg-text-tab__size">{sizeText}</span> : null}
        {view === "text" && lineCount ? <span className="xg-text-tab__lines">{lineCount} 行</span> : null}
        <span className="xg-text-tab__spacer" />
        {view === "text" ? (
          <label className="xg-text-tab__wrap">
            <input type="checkbox" checked={wrapped} onChange={onToggleWrap} data-testid="rightbar-text-wrap" />
            <span>换行</span>
          </label>
        ) : null}
        <button type="button" className="xg-text-tab__action" data-testid="rightbar-text-reload" onClick={() => props.onReload?.()}>
          重载
        </button>
        <button type="button" className="xg-text-tab__action" data-testid="rightbar-text-download" onClick={() => void onDownload()}>
          下载
        </button>
        <button type="button" className="xg-text-tab__action" data-testid="rightbar-text-newtab" onClick={() => void onOpenNewTab()}>
          新标签页打开
        </button>
        <button type="button" className="xg-text-tab__action" data-testid="rightbar-text-copypath" onClick={() => void onCopyPath()}>
          复制服务器路径
        </button>
      </header>

      {notice ? (
        <p className="xg-text-tab__notice" role="status" data-testid="rightbar-text-notice">
          {notice}
        </p>
      ) : null}

      {/* 失败按错误码（§6.5／§6.4 失败语义同族） */}
      {view === "error" ? (
        <p className="xg-text-tab__error" role="alert" data-testid="rightbar-text-error">
          {errorText}
          {errorCode !== null ? <span>（错误码 {errorCode}）</span> : null}
        </p>
      ) : view === "loading" ? (
        <p className="xg-text-tab__hint" data-testid="rightbar-text-loading">
          加载中…
        </p>
      ) : view === "card" ? (
        /* 未支持/超限 ⇒ 下载卡（§6.5 类型覆盖；超限**不截断**） */
        <div className="xg-text-tab__card" data-testid="rightbar-text-card">
          <span className="xg-text-tab__card-icon" aria-hidden="true">
            <XgIcon name="file" />
          </span>
          <div className="xg-text-tab__card-main">
            <span className="xg-text-tab__card-name">{meta.filename || fileId}</span>
            <span className="xg-text-tab__card-reason" data-testid="rightbar-text-card-reason">
              {cardReason}
            </span>
          </div>
          <button type="button" className="xg-text-tab__action" data-testid="rightbar-text-card-download" onClick={() => void onDownload()}>
            下载
          </button>
        </div>
      ) : view === "text" ? (
        /* 文本 / 代码：行号栏（装饰，不入复制文本）＋ 正文 */
        <div className="xg-text-tab__text" data-testid="rightbar-text-body">
          {gutterText ? <div className="xg-text-tab__gutter" aria-hidden="true">{gutterText}</div> : null}
          <pre className={"xg-text-tab__pre" + (wrapped ? " xg-text-tab__pre--wrap" : "")}>{text}</pre>
        </div>
      ) : view === "image" ? (
        /* 图片：仅 Blob URL 进 <img>（§6.5 安全硬规则；SVG 已在模式判定处排除） */
        <div className="xg-text-tab__media" data-testid="rightbar-text-image">
          <img src={blobUrl} alt={meta.filename} />
        </div>
      ) : view === "pdf" ? (
        /* PDF：完整字节交浏览器原生渲染器（一期；`pdfjs` 硬解不在本批） */
        <iframe className="xg-text-tab__frame" data-testid="rightbar-text-pdf" src={blobUrl} title={meta.filename} />
      ) : (
        /* HTML：Blob iframe ＋ sandbox="allow-scripts"（**不含 allow-same-origin**）——脚本无法访问父页 */
        <iframe className="xg-text-tab__frame" data-testid="rightbar-text-html" src={blobUrl} title={meta.filename} sandbox="allow-scripts" />
      )}
      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 逐字译入；真名 token；`xg-text-tab` 前缀隔离） */
import { textTabBodyStyle as STYLE } from "../../styles/inlineStyles";

export default TextTabBody;
